/**
 * Review Settings — admin page (Iter 113)
 *
 * Full-admin-only. Under /admin/tools/review-settings.
 *
 * Two sections:
 *   1. Sub-admin access toggle — flips `review_sub_admin_enabled` in
 *      site_settings. When ON, sub-admins holding `manage_content` can
 *      see the review UI on Oral & Written management pages and flag /
 *      resolve questions. Category CRUD stays full-admin-only either way.
 *   2. Categories — CRUD for the named category pool used by every
 *      "Needs Review" flag on oral + written + bulk-answer CSV import.
 */
import React, { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ClipboardPen, Plus, Pencil, Trash2, Save, Loader2, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const DEFAULT_COLORS = [
  { hex: '#f59e0b', label: 'Amber' },
  { hex: '#3b82f6', label: 'Blue' },
  { hex: '#10b981', label: 'Emerald' },
  { hex: '#ef4444', label: 'Red' },
  { hex: '#8b5cf6', label: 'Violet' },
  { hex: '#64748b', label: 'Slate' },
];

const ReviewSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subAdminEnabled, setSubAdminEnabled] = useState(false);
  const [categories, setCategories] = useState([]);

  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, {id,...} = edit
  const [form, setForm] = useState({ name: '', color: '#f59e0b', description: '' });

  const [deleting, setDeleting] = useState(null); // { category } prompted for delete
  const [reassignTargetId, setReassignTargetId] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cfg, cats] = await Promise.all([
        axios.get(`${API}/api/admin/review/config`, { headers: authHeaders() }),
        axios.get(`${API}/api/admin/review/categories`, { headers: authHeaders() }),
      ]);
      setSubAdminEnabled(!!cfg.data.sub_admin_toggle);
      setCategories(cats.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load review settings');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleSubAdmin = async (next) => {
    setSubAdminEnabled(next); // optimistic
    try {
      await axios.put(`${API}/api/admin/review/config/sub-admin-toggle`, { enabled: next }, { headers: authHeaders() });
      toast.success(next ? 'Sub-admins can now use Needs Review' : 'Sub-admin access disabled');
    } catch (e) {
      setSubAdminEnabled(!next);
      toast.error(e?.response?.data?.detail || 'Failed to save');
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', color: '#f59e0b', description: '' });
    setShowEdit(true);
  };
  const openEdit = (cat) => {
    setEditing(cat);
    setForm({ name: cat.name, color: cat.color || '#f59e0b', description: cat.description || '' });
    setShowEdit(true);
  };

  const submitForm = async () => {
    const name = form.name.trim();
    if (!name) { toast.error('Name is required'); return; }
    if (name.length > 40) { toast.error('Name must be ≤ 40 characters'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(`${API}/api/admin/review/categories/${editing.id}`,
          { name, color: form.color || null, description: form.description || null },
          { headers: authHeaders() });
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/api/admin/review/categories`,
          { name, color: form.color || null, description: form.description || null },
          { headers: authHeaders() });
        toast.success('Category created');
      }
      setShowEdit(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const startDelete = (cat) => {
    setDeleting(cat);
    setReassignTargetId('');
  };

  const performDelete = async ({ forceUncategorize = false } = {}) => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      const params = new URLSearchParams();
      if (forceUncategorize) params.set('force_uncategorize', 'true');
      else if (reassignTargetId) params.set('reassign_to', reassignTargetId);
      await axios.delete(`${API}/api/admin/review/categories/${deleting.id}?${params.toString()}`, { headers: authHeaders() });
      toast.success('Category deleted');
      setDeleting(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleteBusy(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-transition max-w-4xl mx-auto" data-testid="review-settings-page">
        {/* Header */}
        <Card className="mb-6 border-l-4 border-l-amber-500 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-amber-500/5 to-orange-500/5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-500 rounded-lg">
                <ClipboardPen className="w-8 h-8 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl sm:text-3xl text-amber-700" data-testid="review-settings-title">
                  Review Settings
                </CardTitle>
                <CardDescription className="text-sm mt-1">
                  Manage "Needs Review" categories and control sub-admin access.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Sub-admin toggle */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Sub-admin access</CardTitle>
            <CardDescription>
              When ON, sub-admins holding <code className="bg-gray-100 px-1 rounded">manage_content</code> can flag,
              resolve, and see "Needs Review" tags on Oral & Written management pages. Category creation, editing, and
              deletion always stay with full admins.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 rounded-lg bg-amber-50 border border-amber-200">
              <div>
                <div className="font-medium text-sm">Allow sub-admins to use Needs Review</div>
                <div className="text-xs text-gray-600 mt-0.5">Default OFF. Flip when the team is ready.</div>
              </div>
              <Switch checked={subAdminEnabled} onCheckedChange={toggleSubAdmin} data-testid="sub-admin-toggle-switch" />
            </div>
          </CardContent>
        </Card>

        {/* Categories */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <div>
              <CardTitle className="text-base">Categories</CardTitle>
              <CardDescription>Named tags applied when a question is flagged.</CardDescription>
            </div>
            <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700" data-testid="add-category-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Category
            </Button>
          </CardHeader>
          <CardContent>
            {categories.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm" data-testid="no-categories-empty">
                No categories yet. Click "Add Category" to create the first one — e.g. "Claude", "SME Review".
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm" data-testid="categories-table">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Name</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase w-24">Color</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Description</th>
                      <th className="text-center p-3 text-xs font-semibold text-gray-500 uppercase w-24">In Use</th>
                      <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase w-28">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((c) => (
                      <tr key={c.id} className="border-b hover:bg-gray-50/60" data-testid={`category-row-${c.id}`}>
                        <td className="p-3 font-medium">{c.name}</td>
                        <td className="p-3">
                          {c.color ? (
                            <span className="inline-flex items-center gap-1.5 text-xs">
                              <span className="w-4 h-4 rounded-full border" style={{ background: c.color }} />
                              <span className="text-gray-500 font-mono text-[10px]">{c.color}</span>
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="p-3 text-xs text-gray-600 max-w-md truncate">{c.description || '—'}</td>
                        <td className="p-3 text-center">
                          <Badge variant="outline" className="text-xs" data-testid={`usage-badge-${c.id}`}>
                            {c.usage_total} <span className="text-gray-400 ml-1">({c.usage_oral}O·{c.usage_written}W)</span>
                          </Badge>
                        </td>
                        <td className="p-3 text-right">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(c)}
                            className="h-8 w-8 p-0" data-testid={`edit-category-btn-${c.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => startDelete(c)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            data-testid={`delete-category-btn-${c.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create / Edit dialog */}
        <Dialog open={showEdit} onOpenChange={(o) => !o && setShowEdit(false)}>
          <DialogContent data-testid="category-edit-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Category' : 'New Category'}</DialogTitle>
              <DialogDescription>
                Names are case-insensitive. "SME Review" and "sme_review" are treated as the same category.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-xs font-medium text-gray-600">Name *</label>
                <Input value={form.name} maxLength={40}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Claude, SME Review, 1..."
                  data-testid="category-name-input" />
                <div className="text-[10px] text-gray-400 mt-1">{form.name.length}/40</div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Color</label>
                <div className="flex flex-wrap gap-2">
                  {DEFAULT_COLORS.map((c) => (
                    <button key={c.hex} type="button"
                      onClick={() => setForm({ ...form, color: c.hex })}
                      className={`w-8 h-8 rounded-full border-2 transition ${form.color === c.hex ? 'border-gray-900 scale-110' : 'border-gray-200'}`}
                      style={{ background: c.hex }} title={c.label}
                      data-testid={`color-swatch-${c.hex.substring(1)}`} />
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Description (optional)</label>
                <Textarea value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Where this category is used..."
                  rows={2}
                  data-testid="category-description-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEdit(false)} disabled={saving}
                data-testid="category-cancel-btn">Cancel</Button>
              <Button onClick={submitForm} disabled={saving || !form.name.trim()}
                className="bg-amber-600 hover:bg-amber-700" data-testid="category-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editing ? 'Save' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete confirmation with reassignment */}
        <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
          <DialogContent data-testid="category-delete-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Delete "{deleting?.name}"?
              </DialogTitle>
              <DialogDescription>
                {deleting?.usage_total > 0 ? (
                  <>
                    <strong>{deleting.usage_total}</strong> question(s) reference this category
                    (<strong>{deleting.usage_oral}</strong> oral, <strong>{deleting.usage_written}</strong> written).
                    Choose how to handle them before deleting.
                  </>
                ) : (
                  <>This category is not in use — safe to delete.</>
                )}
              </DialogDescription>
            </DialogHeader>
            {deleting?.usage_total > 0 && (
              <div className="space-y-3 py-2">
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
                  <strong>Reassign to another category:</strong> pick a target below.
                </div>
                <Select value={reassignTargetId} onValueChange={setReassignTargetId}>
                  <SelectTrigger data-testid="reassign-target-select">
                    <SelectValue placeholder="— Pick a target category —" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.filter((c) => c.id !== deleting?.id).map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`reassign-option-${c.id}`}>
                        {c.name} <span className="text-gray-400 text-[10px]">({c.usage_total} in use)</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-xs text-gray-500">— or —</div>
                <Button variant="outline" onClick={() => performDelete({ forceUncategorize: true })}
                  disabled={deleteBusy}
                  className="w-full text-red-700 border-red-200 hover:bg-red-50"
                  data-testid="force-uncategorize-btn">
                  Move all {deleting.usage_total} to Uncategorized, then delete
                </Button>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleting(null)} disabled={deleteBusy}
                data-testid="delete-cancel-btn">Cancel</Button>
              <Button
                onClick={() => performDelete()}
                disabled={deleteBusy || (deleting?.usage_total > 0 && !reassignTargetId)}
                className="bg-red-600 hover:bg-red-700"
                data-testid="delete-confirm-btn">
                {deleteBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ReviewSettingsPage;
